import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2, GripVertical, Loader2, FileText, HelpCircle, Edit3, X, Check, RefreshCw, ExternalLink, AlertTriangle, Home, Leaf, Image, Mail, Globe, type } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader } from './ui/card';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = '/api';

// Simple Markdown Preview component
const MarkdownPreview = ({ content }) => {
  const parseMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^- (.*?)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div 
      className="prose prose-sm max-w-none text-muted-foreground"
      dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }}
    />
  );
};

// Character count component with warning
const CharacterCount = ({ value, max, type = "normal" }) => {
  const count = (value || '').length;
  const isOver = max && count > max;
  const isNearLimit = max && count > max * 0.9;
  
  return (
    <div className={`text-xs flex items-center gap-1 ${isOver ? 'text-destructive' : isNearLimit ? 'text-orange-500' : 'text-muted-foreground'}`}>
      {isOver && <AlertTriangle size={12} />}
      {count}{max ? `/${max}` : ''} characters
      {type === "meta" && isOver && <span className="ml-1">(SEO: keep under {max})</span>}
    </div>
  );
};

// Sortable FAQ Item
const SortableFAQItem = ({ faq, onUpdate, onDelete, token }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [question, setQuestion] = useState(faq.question);
  const [answer, setAnswer] = useState(faq.answer);
  const [active, setActive] = useState(faq.active !== false);
  const [isSaving, setIsSaving] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const [username, password] = token.split(':');
      await axios.put(
        `${API}/faqs/${faq.id}`,
        { question, answer, active },
        { auth: { username, password } }
      );
      onUpdate(faq.id, { question, answer, active });
      setIsEditing(false);
      toast.success('FAQ updated');
    } catch (error) {
      toast.error('Failed to update FAQ');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this FAQ?')) return;
    
    try {
      const [username, password] = token.split(':');
      await axios.delete(`${API}/faqs/${faq.id}`, {
        auth: { username, password }
      });
      onDelete(faq.id);
      toast.success('FAQ deleted');
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  return (
    <div ref={setNodeRef} style={style} className={`bg-white border rounded-lg p-4 mb-3 ${!active ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <div
          {...attributes}
          {...listeners}
          className="mt-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        >
          <GripVertical size={20} />
        </div>
        
        <div className="flex-1">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Question</Label>
                <Input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Answer (Markdown supported)</Label>
                <Textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows={4}
                  className="mt-1 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use **bold**, *italic*, and - for bullet points
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`active-${faq.id}`}
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor={`active-${faq.id}`} className="text-sm">Active (visible on site)</Label>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium flex items-center gap-2">
                    <HelpCircle className="text-primary" size={16} />
                    {faq.question}
                    {!active && <span className="text-xs bg-muted px-2 py-0.5 rounded">Hidden</span>}
                  </h4>
                  <div className="mt-2 text-sm">
                    <MarkdownPreview content={faq.answer} />
                  </div>
                </div>
                <div className="flex gap-1 ml-4">
                  <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
                    <Edit3 size={16} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleDelete} className="text-destructive hover:text-destructive">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Content Field Editor with character count
const ContentField = ({ label, contentKey, value, contentType, onUpdate, token, multiline = false, maxChars = null, page = null }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const [username, password] = token.split(':');
      await axios.put(
        `${API}/content/${contentKey}`,
        { key: contentKey, value: editValue, content_type: contentType, page, label },
        { auth: { username, password } }
      );
      onUpdate(contentKey, editValue);
      setIsEditing(false);
      toast.success(`${label} updated`);
    } catch (error) {
      toast.error('Failed to update content');
    } finally {
      setIsSaving(false);
    }
  };

  const isMeta = contentType === 'meta';

  return (
    <div className="border rounded-lg p-4 bg-white" data-testid={`content-field-${contentKey}`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="font-medium">{label}</Label>
        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded font-mono">
          {contentKey}
        </span>
      </div>
      
      {isEditing ? (
        <div className="space-y-2">
          {multiline || isMeta ? (
            <Textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              rows={isMeta ? 3 : 4}
              className={`text-sm ${isMeta ? '' : 'font-mono'}`}
            />
          ) : (
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
            />
          )}
          <div className="flex items-center justify-between">
            <CharacterCount value={editValue} max={maxChars} type={isMeta ? "meta" : "normal"} />
            {contentType === 'markdown' && (
              <p className="text-xs text-muted-foreground">
                Markdown: **bold**, *italic*, - bullet points
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setIsEditing(false); setEditValue(value || ''); }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div 
          className="p-3 bg-muted/50 rounded cursor-pointer hover:bg-muted transition-colors group"
          onClick={() => setIsEditing(true)}
        >
          {contentType === 'markdown' ? (
            <MarkdownPreview content={value} />
          ) : (
            <p className="text-sm whitespace-pre-wrap">{value || <span className="text-muted-foreground italic">Click to edit...</span>}</p>
          )}
          <div className="flex items-center justify-between mt-2">
            <CharacterCount value={value} max={maxChars} type={isMeta ? "meta" : "normal"} />
            <Edit3 className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      )}
    </div>
  );
};

// Preview Link component
const PreviewLink = ({ path, label }) => (
  <a 
    href={path} 
    target="_blank" 
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
  >
    <ExternalLink size={14} />
    Preview {label}
  </a>
);

const ContentManager = ({ token }) => {
  const [content, setContent] = useState({});
  const [faqs, setFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSeeding, setIsSeeding] = useState(false);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [isAddingFaq, setIsAddingFaq] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/content/all`);
      // Convert flat format to our expected format
      const contentDict = {};
      
      // First, add flat content (includes legacy keys with values)
      if (response.data.flat) {
        Object.entries(response.data.flat).forEach(([key, value]) => {
          contentDict[key] = { key, value };
        });
      }
      
      // Then, merge with by_page content (which has full metadata)
      if (response.data.by_page) {
        Object.values(response.data.by_page).forEach(pageContent => {
          Object.entries(pageContent).forEach(([key, item]) => {
            contentDict[key] = item;
          });
        });
      }
      
      setContent(contentDict);
      setFaqs(response.data.faqs || []);
    } catch (error) {
      console.error('Error fetching content:', error);
      // Fallback to old endpoint
      try {
        const fallback = await axios.get(`${API}/content`);
        setContent(fallback.data.content || {});
        setFaqs(fallback.data.faqs || []);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSeedContent = async () => {
    setIsSeeding(true);
    try {
      const [username, password] = token.split(':');
      const response = await axios.post(`${API}/content/seed`, {}, {
        auth: { username, password }
      });
      toast.success(response.data.message);
      fetchContent();
    } catch (error) {
      toast.error('Failed to seed content');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleContentUpdate = (key, value) => {
    setContent(prev => ({
      ...prev,
      [key]: { ...prev[key], value }
    }));
  };

  const handleFaqUpdate = (id, updates) => {
    setFaqs(prev => prev.map(faq => 
      faq.id === id ? { ...faq, ...updates } : faq
    ));
  };

  const handleFaqDelete = (id) => {
    setFaqs(prev => prev.filter(faq => faq.id !== id));
  };

  const handleAddFaq = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      toast.error('Please fill in both question and answer');
      return;
    }

    setIsAddingFaq(true);
    try {
      const [username, password] = token.split(':');
      const response = await axios.post(`${API}/faqs`, newFaq, {
        auth: { username, password }
      });
      setFaqs(prev => [...prev, response.data.faq]);
      setNewFaq({ question: '', answer: '' });
      toast.success('FAQ added');
    } catch (error) {
      toast.error('Failed to add FAQ');
    } finally {
      setIsAddingFaq(false);
    }
  };

  const handleFaqDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = faqs.findIndex(faq => faq.id === active.id);
    const newIndex = faqs.findIndex(faq => faq.id === over.id);
    const newFaqs = arrayMove(faqs, oldIndex, newIndex);
    setFaqs(newFaqs);

    try {
      const [username, password] = token.split(':');
      await axios.put(
        `${API}/faqs/reorder`,
        newFaqs.map(f => f.id),
        { auth: { username, password } }
      );
      toast.success('FAQ order updated');
    } catch (error) {
      toast.error('Failed to reorder FAQs');
      fetchContent();
    }
  };

  // Helper to get content value with legacy key fallback
  // Maps new key format to legacy format: homepage_hero_title -> hero_title
  const c = (key) => {
    // Check new key first
    if (content[key]?.value) return content[key].value;
    
    // Try legacy key (strip homepage_ prefix for homepage keys)
    const legacyKeyMappings = {
      'homepage_hero_title': 'hero_title',
      'homepage_hero_subtitle': 'hero_subtitle',
      'homepage_story_heading': 'story_title',
      'homepage_story_paragraph_1': 'story_text_1',
      'homepage_story_paragraph_2': 'story_text_2',
      'homepage_highlights_heading': 'highlights_title',
      'homepage_highlight_1_title': 'highlight_1_title',
      'homepage_highlight_1_description': 'highlight_1_text',
      'homepage_highlight_2_title': 'highlight_2_title',
      'homepage_highlight_2_description': 'highlight_2_text',
      'homepage_highlight_3_title': 'highlight_3_title',
      'homepage_highlight_3_description': 'highlight_3_text',
      'homepage_reviews_heading': 'reviews_title',
      'homepage_faq_heading': 'faq_title',
      'homepage_faq_subheading': 'faq_subtitle',
    };
    
    const legacyKey = legacyKeyMappings[key];
    if (legacyKey && content[legacyKey]?.value) {
      return content[legacyKey].value;
    }
    
    return '';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const hasContent = Object.keys(content).length > 0 || faqs.length > 0;

  return (
    <div className="space-y-6" data-testid="content-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-playfair text-2xl font-semibold">Content Manager</h2>
          <p className="text-muted-foreground">Edit all text content on the public website</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchContent}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {!hasContent && (
            <Button onClick={handleSeedContent} disabled={isSeeding}>
              {isSeeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Initialize Default Content
            </Button>
          )}
        </div>
      </div>

      {!hasContent ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No Content Found</h3>
            <p className="text-muted-foreground mb-4">
              Click "Initialize Default Content" to populate the database with editable content for all pages.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="homepage" className="space-y-4">
          <TabsList className="flex flex-wrap gap-1">
            <TabsTrigger value="homepage" className="flex items-center gap-1">
              <Home size={14} />
              Homepage
            </TabsTrigger>
            <TabsTrigger value="garden" className="flex items-center gap-1">
              <Leaf size={14} />
              Garden
            </TabsTrigger>
            <TabsTrigger value="gallery" className="flex items-center gap-1">
              <Image size={14} />
              Gallery
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-1">
              <Mail size={14} />
              Contact
            </TabsTrigger>
            <TabsTrigger value="footer" className="flex items-center gap-1">
              <FileText size={14} />
              Footer
            </TabsTrigger>
            <TabsTrigger value="seo" className="flex items-center gap-1">
              <Globe size={14} />
              SEO
            </TabsTrigger>
            <TabsTrigger value="faqs" className="flex items-center gap-1">
              <HelpCircle size={14} />
              FAQs ({faqs.length})
            </TabsTrigger>
          </TabsList>

          {/* Homepage Tab */}
          <TabsContent value="homepage" className="space-y-4">
            <div className="flex justify-end">
              <PreviewLink path="/" label="Homepage" />
            </div>
            
            <Card>
              <CardHeader>
                <h3 className="font-semibold">Hero Section</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Hero Title (H1)"
                  contentKey="homepage_hero_title"
                  value={c('homepage_hero_title')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="homepage"
                />
                <ContentField
                  label="Hero Subtitle"
                  contentKey="homepage_hero_subtitle"
                  value={c('homepage_hero_subtitle')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="homepage"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Story Section</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Story Heading (H2)"
                  contentKey="homepage_story_heading"
                  value={c('homepage_story_heading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="homepage"
                />
                <ContentField
                  label="Story Paragraph 1"
                  contentKey="homepage_story_paragraph_1"
                  value={c('homepage_story_paragraph_1')}
                  contentType="markdown"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="homepage"
                />
                <ContentField
                  label="Story Paragraph 2"
                  contentKey="homepage_story_paragraph_2"
                  value={c('homepage_story_paragraph_2')}
                  contentType="markdown"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="homepage"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">SEO Description Paragraph</h3>
                <p className="text-sm text-muted-foreground">This text is visible to search engines and AI crawlers</p>
              </CardHeader>
              <CardContent>
                <ContentField
                  label="SEO Paragraph"
                  contentKey="homepage_seo_paragraph"
                  value={c('homepage_seo_paragraph')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="homepage"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Highlights Section</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Highlights Heading"
                  contentKey="homepage_highlights_heading"
                  value={c('homepage_highlights_heading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="homepage"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <ContentField
                      label="Highlight 1 Title"
                      contentKey="homepage_highlight_1_title"
                      value={c('homepage_highlight_1_title')}
                      contentType="text"
                      onUpdate={handleContentUpdate}
                      token={token}
                      page="homepage"
                    />
                    <ContentField
                      label="Highlight 1 Description"
                      contentKey="homepage_highlight_1_description"
                      value={c('homepage_highlight_1_description')}
                      contentType="text"
                      onUpdate={handleContentUpdate}
                      token={token}
                      multiline
                      page="homepage"
                    />
                  </div>
                  <div className="space-y-2">
                    <ContentField
                      label="Highlight 2 Title"
                      contentKey="homepage_highlight_2_title"
                      value={c('homepage_highlight_2_title')}
                      contentType="text"
                      onUpdate={handleContentUpdate}
                      token={token}
                      page="homepage"
                    />
                    <ContentField
                      label="Highlight 2 Description"
                      contentKey="homepage_highlight_2_description"
                      value={c('homepage_highlight_2_description')}
                      contentType="text"
                      onUpdate={handleContentUpdate}
                      token={token}
                      multiline
                      page="homepage"
                    />
                  </div>
                  <div className="space-y-2">
                    <ContentField
                      label="Highlight 3 Title"
                      contentKey="homepage_highlight_3_title"
                      value={c('homepage_highlight_3_title')}
                      contentType="text"
                      onUpdate={handleContentUpdate}
                      token={token}
                      page="homepage"
                    />
                    <ContentField
                      label="Highlight 3 Description"
                      contentKey="homepage_highlight_3_description"
                      value={c('homepage_highlight_3_description')}
                      contentType="text"
                      onUpdate={handleContentUpdate}
                      token={token}
                      multiline
                      page="homepage"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Reviews & FAQ Sections</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Reviews Heading"
                  contentKey="homepage_reviews_heading"
                  value={c('homepage_reviews_heading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="homepage"
                />
                <ContentField
                  label="FAQ Heading"
                  contentKey="homepage_faq_heading"
                  value={c('homepage_faq_heading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="homepage"
                />
                <ContentField
                  label="FAQ Subheading"
                  contentKey="homepage_faq_subheading"
                  value={c('homepage_faq_subheading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="homepage"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Garden Tab */}
          <TabsContent value="garden" className="space-y-4">
            <div className="flex justify-end">
              <PreviewLink path="/garden" label="Garden Page" />
            </div>
            
            <Card>
              <CardHeader>
                <h3 className="font-semibold">Hero Section</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Hero Title (H1)"
                  contentKey="garden_hero_title"
                  value={c('garden_hero_title')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="garden"
                />
                <ContentField
                  label="Hero Subtitle"
                  contentKey="garden_hero_subtitle"
                  value={c('garden_hero_subtitle')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="garden"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">History Section</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="History Heading (H2)"
                  contentKey="garden_history_heading"
                  value={c('garden_history_heading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="garden"
                />
                <ContentField
                  label="History Paragraph 1"
                  contentKey="garden_history_paragraph_1"
                  value={c('garden_history_paragraph_1')}
                  contentType="markdown"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="garden"
                />
                <ContentField
                  label="History Paragraph 2"
                  contentKey="garden_history_paragraph_2"
                  value={c('garden_history_paragraph_2')}
                  contentType="markdown"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="garden"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Features Section</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Features Heading"
                  contentKey="garden_features_heading"
                  value={c('garden_features_heading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="garden"
                />
                <ContentField
                  label="SEO Description Paragraph"
                  contentKey="garden_seo_paragraph"
                  value={c('garden_seo_paragraph')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="garden"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="space-y-4">
            <div className="flex justify-end">
              <PreviewLink path="/gallery" label="Gallery Page" />
            </div>
            
            <Card>
              <CardHeader>
                <h3 className="font-semibold">Gallery Page Content</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Gallery Heading (H1)"
                  contentKey="gallery_heading"
                  value={c('gallery_heading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="gallery"
                />
                <ContentField
                  label="Gallery Description"
                  contentKey="gallery_description"
                  value={c('gallery_description')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="gallery"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact" className="space-y-4">
            <div className="flex justify-end">
              <PreviewLink path="/contact" label="Contact Page" />
            </div>
            
            <Card>
              <CardHeader>
                <h3 className="font-semibold">Contact Page Content</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Contact Heading (H1)"
                  contentKey="contact_heading"
                  value={c('contact_heading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="contact"
                />
                <ContentField
                  label="Contact Description"
                  contentKey="contact_description"
                  value={c('contact_description')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="contact"
                />
                <ContentField
                  label="Form Heading"
                  contentKey="contact_form_heading"
                  value={c('contact_form_heading')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="contact"
                />
                <ContentField
                  label="Form Description"
                  contentKey="contact_form_description"
                  value={c('contact_form_description')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="contact"
                />
                <ContentField
                  label="Hosts Text"
                  contentKey="contact_hosts_text"
                  value={c('contact_hosts_text')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="contact"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Footer Tab */}
          <TabsContent value="footer" className="space-y-4">
            <Card>
              <CardHeader>
                <h3 className="font-semibold">Footer Content</h3>
                <p className="text-sm text-muted-foreground">Appears on all pages</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Footer Tagline"
                  contentKey="footer_tagline"
                  value={c('footer_tagline')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="footer"
                />
                <ContentField
                  label="Footer Address"
                  contentKey="footer_address"
                  value={c('footer_address')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="footer"
                />
                <ContentField
                  label="Copyright Text"
                  contentKey="footer_copyright"
                  value={c('footer_copyright')}
                  contentType="text"
                  onUpdate={handleContentUpdate}
                  token={token}
                  page="footer"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* SEO Tab */}
          <TabsContent value="seo" className="space-y-4">
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="text-orange-500 mt-0.5" size={18} />
                  <div className="text-sm">
                    <p className="font-medium text-orange-700">SEO Best Practices</p>
                    <p className="text-orange-600">Keep meta descriptions under 160 characters for optimal display in search results.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Homepage SEO</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Meta Title"
                  contentKey="meta_title_homepage"
                  value={c('meta_title_homepage')}
                  contentType="meta"
                  onUpdate={handleContentUpdate}
                  token={token}
                  maxChars={60}
                  page="seo"
                />
                <ContentField
                  label="Meta Description"
                  contentKey="meta_description_homepage"
                  value={c('meta_description_homepage')}
                  contentType="meta"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  maxChars={160}
                  page="seo"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Garden Page SEO</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Meta Title"
                  contentKey="meta_title_garden"
                  value={c('meta_title_garden')}
                  contentType="meta"
                  onUpdate={handleContentUpdate}
                  token={token}
                  maxChars={60}
                  page="seo"
                />
                <ContentField
                  label="Meta Description"
                  contentKey="meta_description_garden"
                  value={c('meta_description_garden')}
                  contentType="meta"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  maxChars={160}
                  page="seo"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Gallery Page SEO</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Meta Title"
                  contentKey="meta_title_gallery"
                  value={c('meta_title_gallery')}
                  contentType="meta"
                  onUpdate={handleContentUpdate}
                  token={token}
                  maxChars={60}
                  page="seo"
                />
                <ContentField
                  label="Meta Description"
                  contentKey="meta_description_gallery"
                  value={c('meta_description_gallery')}
                  contentType="meta"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  maxChars={160}
                  page="seo"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Contact Page SEO</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <ContentField
                  label="Meta Title"
                  contentKey="meta_title_contact"
                  value={c('meta_title_contact')}
                  contentType="meta"
                  onUpdate={handleContentUpdate}
                  token={token}
                  maxChars={60}
                  page="seo"
                />
                <ContentField
                  label="Meta Description"
                  contentKey="meta_description_contact"
                  value={c('meta_description_contact')}
                  contentType="meta"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  maxChars={160}
                  page="seo"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold">Schema.org Description</h3>
                <p className="text-sm text-muted-foreground">Used in JSON-LD structured data for search engines</p>
              </CardHeader>
              <CardContent>
                <ContentField
                  label="Schema Description"
                  contentKey="schema_description"
                  value={c('schema_description')}
                  contentType="meta"
                  onUpdate={handleContentUpdate}
                  token={token}
                  multiline
                  page="seo"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQs Tab */}
          <TabsContent value="faqs" className="space-y-4">
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <HelpCircle className="text-blue-500 mt-0.5" size={18} />
                  <div className="text-sm">
                    <p className="font-medium text-blue-700">FAQ Schema</p>
                    <p className="text-blue-600">FAQs are automatically included in the FAQPage JSON-LD schema for rich search results. Drag to reorder.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">FAQ Management</h3>
                    <p className="text-sm text-muted-foreground">Add, edit, reorder, or delete FAQs. Changes update the schema immediately.</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Add New FAQ */}
                <div className="bg-muted/50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Plus size={18} />
                    Add New FAQ
                  </h4>
                  <div className="space-y-3">
                    <Input
                      placeholder="Question"
                      value={newFaq.question}
                      onChange={(e) => setNewFaq(prev => ({ ...prev, question: e.target.value }))}
                      data-testid="new-faq-question"
                    />
                    <Textarea
                      placeholder="Answer (Markdown supported: **bold**, *italic*, - bullet points)"
                      value={newFaq.answer}
                      onChange={(e) => setNewFaq(prev => ({ ...prev, answer: e.target.value }))}
                      rows={3}
                      data-testid="new-faq-answer"
                    />
                    <Button onClick={handleAddFaq} disabled={isAddingFaq} data-testid="add-faq-button">
                      {isAddingFaq ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                      Add FAQ
                    </Button>
                  </div>
                </div>

                {/* FAQ List */}
                {faqs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No FAQs yet. Add your first question above.</p>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFaqDragEnd}>
                    <SortableContext items={faqs.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      {faqs.map(faq => (
                        <SortableFAQItem
                          key={faq.id}
                          faq={faq}
                          onUpdate={handleFaqUpdate}
                          onDelete={handleFaqDelete}
                          token={token}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default ContentManager;
